import { useQuery } from '@tanstack/react-query';
import { fetchUrl } from '@utils/api';

// Cache configuration to prevent memory bloat
const CACHE_STALE_TIME = 30 * 60 * 1000; // 30 minutes - data considered fresh
const CACHE_GC_TIME = 60 * 60 * 1000; // 1 hour - data garbage collected

/**
 * Custom hook to fetch GeoJSON data from S3
 *
 * Features:
 * - Fetches and returns GeoJSON data
 * - Caches data for 30 minutes to reduce API calls
 *
 * @param {string} queryKey - Unique key for the query
 * @param {string} uuid - Project UUID
 * @param {string} path - S3 path to GeoJSON file
 * @param {boolean} enabled - Whether the query should run
 * @returns {Object} React Query result with data, error, isFetching
 */
export const useGeoJSONData = (queryKey, uuid, path, enabled) => {
	return useQuery({
		queryKey: [queryKey, uuid, path],
		queryFn: async () => {
			// Fetch signed URL from backend
			const query = new URLSearchParams({ key: path });
			const data = await fetchUrl(`fileLink?${query.toString()}`);
			// const data = await fetchUrl(`fileLink?key=${path}`);
			if (data?.url) {
				// Fetch actual GeoJSON from S3
				const response = await fetch(data.url);
				if (!response.ok) throw new Error(`Failed to fetch ${queryKey}`);

				return await response.json();
			}
			throw new Error(`No ${queryKey} URL`);
		},
		enabled: !!uuid && !!path && enabled,
		refetchOnWindowFocus: false,
		staleTime: CACHE_STALE_TIME,
		gcTime: CACHE_GC_TIME,
	});
};
